import { Injectable } from '@angular/core';

import { Observable, throwError } from 'rxjs';
import { map, catchError, shareReplay } from 'rxjs/operators';

import { RestService } from '../../../services/rest/rest.service';
import { SecurityService } from '../../../services/security.service';
import { MarketInfoNewsRestService } from '../../../services/market-info/market-info-news-rest-service';

import {
  EventsData,
  MarketOutlook,
  EventsResponse,
  EventProperty,
  NewsContent,
  EventType,
  EventPeriod,
  BaseMarketEvent,
  EventTypeHelper
} from './market-info.domain';

@Injectable()
export class MarketInfoEventsRestService {

  private static readonly CACHE_CAPACITY: number = 1;
  private static readonly PERMISSION_HOME_PAGE_EVENTS_RATINGS: string = 'HOME_PAGE_EVENTS_RATINGS';
  private static readonly ALL_EVENT_TYPES: Array<EventType> =
    [EventType.NEWS, EventType.RATING_CHANGES, EventType.DIVIDENDS, EventType.EARNINGS];

  private readonly ORDERED_EVENTS: Array<EventProperty> = ['earnings', 'dividends'];
  private cacheMap: Map<string, Observable<EventsData>>;

  constructor(private restService: RestService,
              private securityService: SecurityService,
              private marketInfoNewsRestService: MarketInfoNewsRestService) {
    if (this.securityService.hasPermission(MarketInfoEventsRestService.PERMISSION_HOME_PAGE_EVENTS_RATINGS)) {
      this.ORDERED_EVENTS.push('ratingsChange');
    }
    this.cacheMap = new Map<string, Observable<EventsData>>();
  }

  /**
   * Retrieve market outlook (reports & news, indicators)
   * marketIndicators are not returned when markets are open
   */
  getMarketOutlook(): Observable<MarketOutlook> {
    return this.marketInfoNewsRestService.getMarketOutlook();
  }

  /**
   * @param eventTypes
   * @param period
   */
  getEvents(eventTypes: Array<EventType>, period?: EventPeriod): Observable<EventsData> {
    const eventType = this.filterEventTypes(eventTypes);
    const cacheKey = this.createCacheKey(eventType, period);

    if (!this.cacheMap.has(cacheKey)) {
      this.cacheMap.set(
        cacheKey,
        this.restService.getResource('portfolio.events')
          .get<EventsResponse>({
            params: { eventType, period }
          })
          .pipe(
            map((response: EventsResponse): EventsData => this.packageEventData(response)),
            catchError((error: unknown): Observable<never> => {
              this.cacheMap.delete(period);
              return throwError(() => error);
            }),
            shareReplay(MarketInfoEventsRestService.CACHE_CAPACITY)
          )
      );
    }

    return this.cacheMap.get(cacheKey);
  }

  getNewsItem(storyId: string): Observable<NewsContent> {
    return this.marketInfoNewsRestService.getNewsItem(storyId);
  }

  private filterEventTypes(eventTypesArray: Array<EventType>): Array<string> {
    let index;
    const eventTypes = eventTypesArray instanceof Array ? eventTypesArray : MarketInfoEventsRestService.ALL_EVENT_TYPES;
    if (!this.securityService.hasPermission(MarketInfoEventsRestService.PERMISSION_HOME_PAGE_EVENTS_RATINGS)) {
      index = eventTypes.indexOf(EventType.RATING_CHANGES);
      if (index > -1) {
        eventTypes.splice(index, 1);
      }
    }
    return eventTypes;
  }

  private packageEventData(response: EventsResponse): EventsData {
    const events = {};

    this.ORDERED_EVENTS.forEach((eventType: EventProperty): void => {
      if (response[eventType] !== undefined ) {
        events[eventType] = response[eventType];
        events[eventType].forEach((event: BaseMarketEvent): void => {
          event.securityEventType = EventTypeHelper.toUpperCase(eventType);
        });
      }
    });
    return {
      dateRange: {
        fromDate: response.fromDate,
        toDate: response.toDate
      },
      events
    };
  }

  private createCacheKey(events: Array<string>, period?: EventPeriod): string {
    let key = events.join(',');
    key = key + (period === undefined ? '' : period);
    return key;
  }
}

